import { Outlet } from "react-router-dom"
import { Scale } from "lucide-react"

import { PageHeader } from "@/components/PageHeader"
import { RequireEntity } from "@/components/layout/RequireEntity"
import { Toolbar, ToolbarField, ToolbarNote } from "@/components/Toolbar"
import { Input } from "@/components/ui/input"
import { SelectorDeFecha } from "@/components/SelectorDeFecha"
import { Label } from "@/components/ui/label"
import { BOOK_LABELS, type BookId } from "@/lib/types"
import { cn } from "@/lib/utils"

import { BooksFiltersProvider, useBooksFilters } from "@/routes/books/context"
import { SeccionTabs } from "@/components/SeccionTabs"
import { t } from "@/lib/i18n"

const BOOK_IDS = Object.keys(BOOK_LABELS) as BookId[]

/**
 * Los tres estados CONTABLES.
 *
 * Seccion aparte de "Libros" a proposito. El diario, el mayor y el balance de
 * comprobacion son el MATERIAL DE TRABAJO del contador; estos tres son el
 * ENTREGABLE. Meterlos como dos pestanas mas de la misma vista los presentaria
 * como otra forma de mirar lo mismo, y no lo son: a un tercero se le entrega
 * esto, no el mayor.
 *
 * Comparten el selector de libro y el rango con los libros —son los mismos
 * filtros sobre los mismos asientos— y por eso reusan `BooksFiltersProvider` en
 * vez de duplicar un contexto que se desincronizaria.
 */
function StatementsTabs() {
  const tabs = [
    { to: "situacion", label: "Balance general" },
    { to: "resultados", label: "Estado de resultados" },
    { to: "flujo", label: "Flujo de efectivo" },
  ]

  return (
    <SeccionTabs tabs={tabs} ariaLabel="Estados contables" />
  )
}

function StatementsToolbar() {
  const { book, setBook, from, setFrom, to, setTo } = useBooksFilters()

  return (
    <Toolbar>
      <div className="space-y-1">
        <Label htmlFor="libro-estados">{t("Libro")}</Label>
        <select
          id="libro-estados"
          value={book}
          onChange={(event) => setBook(event.target.value as BookId)}
          className={cn(
            "h-7 rounded-md border bg-surface px-2 text-xs",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          {BOOK_IDS.map((id) => (
            <option key={id} value={id}>
              {BOOK_LABELS[id]}
            </option>
          ))}
        </select>
      </div>

      <div className="hidden self-stretch border-l md:block" aria-hidden="true" />

      <ToolbarField label={t("Desde")} htmlFor="estados-desde">
        <SelectorDeFecha
          id="estados-desde"
          value={from}
          onChange={setFrom}
          className="w-[8.5rem]"
        />
      </ToolbarField>

      <ToolbarField label={t("Hasta / corte")} htmlFor="estados-hasta">
        <SelectorDeFecha
          id="estados-hasta"
          value={to}
          onChange={setTo}
          className="w-[8.5rem]"
        />
      </ToolbarField>

      <ToolbarNote icon={Scale} className="ml-auto">
        {t("El balance general usa solo la fecha de corte. El resultado y el flujo usan el rango.")}</ToolbarNote>
    </Toolbar>
  )
}

export function StatementsLayout() {
  return (
    <BooksFiltersProvider>
      <div className="space-y-3">
        <PageHeader
          eyebrow="Vista contable"
          title={t("Estados contables")}
          description={t("Salen de los asientos, siguen el plan de cuentas y tienen que cuadrar. No son los estados financieros de la seccion Finanzas: aquellos salen del modelo, admiten criterio propio y se editan. Los dos pueden discrepar, y esa diferencia se mira en Finanzas › Finanzas vs Contabilidad.")}
        />

        <StatementsTabs />
        <StatementsToolbar />

        <RequireEntity que="sus estados contables">
          <Outlet />
        </RequireEntity>
      </div>
    </BooksFiltersProvider>
  )
}
